// Turns stored question text (rich-text-editor HTML mixed with LaTeX) into render segments:
// { type: 'text' | 'inlineMath' | 'displayMath', content } and { type: 'image', url, alt }.

const ENTITIES = {
  amp: '&', nbsp: ' ', quot: '"', '#39': "'",
  le: '≤', ge: '≥', ne: '≠', alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ε', theta: 'θ',
  lambda: 'λ', mu: 'μ', pi: 'π', sigma: 'σ', omega: 'ω', infin: '∞', plusmn: '±', times: '×',
  divide: '÷', sdot: '⋅', rArr: '⇒', lArr: '⇐', hArr: '⇔', rarr: '→', larr: '←', harr: '↔',
};

// Only real editor tags count as HTML, so "$x<y$ and $y>z$" is left alone
const TAG = String.raw`<\/?(?:p|br|div|span|b|strong|i|em|u|sup|sub|img|li|ul|ol|table|tbody|thead|tr|td|th|h[1-6])\b[^>]*>`;
const HAS_TAG = new RegExp(TAG, 'i');

// Unicode, not LaTeX commands: the result is correct both inside and outside math.
// &lt; / &gt; are decoded last so an escaped "&lt;b&gt;" is never mistaken for a tag.
const decodeEntities = (s) => s
  .replace(/&([a-zA-Z]+|#39);/g, (m, name) => ENTITIES[name] ?? ENTITIES[name.toLowerCase()] ?? m)
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>');

const htmlToLatex = (str) => {
  if (!HAS_TAG.test(str)) return decodeEntities(str);

  let result = str
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|tr|h[1-6])>/gi, '\n');

  const wrap = (inner, open) => {
    const i = inner.trim();
    return /\$|\\\(|\\begin\{/.test(i) ? i : `$${open}{${i}}$`;
  };
  result = result
    .replace(/<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>/gi, (_, i) => wrap(i, '\\textbf'))
    .replace(/<(?:em|i)>([\s\S]*?)<\/(?:em|i)>/gi, (_, i) => wrap(i, '\\textit'))
    .replace(/<u>([\s\S]*?)<\/u>/gi, (_, i) => wrap(i, '\\underline'))
    .replace(/<sup>([\s\S]*?)<\/sup>/gi, (_, i) => wrap(i, '^'))
    .replace(/<sub>([\s\S]*?)<\/sub>/gi, (_, i) => wrap(i, '_'))
    .replace(/<img[^>]+src=["']([^"']+)["'][^>]*\/?>/gi, (_, src) => `![image](${src})`)
    .replace(new RegExp(TAG, 'gi'), '');

  return decodeEntities(result).replace(/\n{3,}/g, '\n\n').trim();
};

const FORWARD_SLASH_LATEX = /\/(?:begin|end|frac|sqrt|sum|int|alpha|beta|gamma|delta|theta|lambda|mu|pi|sigma|omega|text|mathbf|mathrm|left|right|cdot|times|leq|geq|neq|pm|infty|rightarrow|leftarrow|Rightarrow|Leftarrow|vec|hat|bar|overline|underline|array|matrix|pmatrix|bmatrix|cases|align|\\)\b/;

const normalizeLatex = (str) => {
  // Some Firestore data stores LaTeX commands with a forward slash instead of a backslash
  // e.g. "/begin{array}" → "\begin{array}", "/frac{a}{b}" → "\frac{a}{b}"
  if (FORWARD_SLASH_LATEX.test(str) && !/\\(?:begin|end|frac|sqrt|[a-zA-Z]+)/.test(str)) {
    str = str.replace(/\/([a-zA-Z]+\{)/g, '\\$1')
      .replace(/\/([a-zA-Z]+)(?=[\s$\\,;.{}^_[\]()!]|$)/g, '\\$1');
  }

  // Double-escaped data ("\\frac") has no single-backslash commands at all. Only then collapse,
  // otherwise "a \\b" inside an array is a row break followed by "b", not the command \b.
  if (!/(^|[^\\])\\[a-zA-Z]/.test(str)) str = str.replace(/\\\\([a-zA-Z])/g, '\\$1');

  str = str.replace(/\\(begin|end)\s*\{\s*([^}]+?)\s*\}/g, '\\$1{$2}');

  // KaTeX fails on nested display delimiters, e.g. \[ \begin{array} ... \end{array} \].
  // The segmenter extracts \begin environments itself, so the outer delimiters can go.
  str = str.replace(/\\\[\s*(\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\})\s*\\\]/g, '$1');
  str = str.replace(/\$\$\s*(\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\})\s*\$\$/g, '$1');

  // KaTeX does NOT support LaTeX's *{N}{col} column repetition syntax in arrays.
  return str.replace(/\*\{(\d+)\}\{([^}]+)\}/g, (_, n, col) => col.repeat(parseInt(n, 10)));
};

// Next "$" that isn't an escaped "\$"
const nextDollar = (str, from) => {
  for (let j = str.indexOf('$', from); j !== -1; j = str.indexOf('$', j + 1)) {
    if (str[j - 1] !== '\\') return j;
  }
  return -1;
};

export const parseRichText = (input) => {
  const str = normalizeLatex(htmlToLatex(String(input)));
  const segments = [];
  let i = 0;
  let textBuffer = '';

  const flushText = () => {
    if (textBuffer) {
      segments.push({ type: 'text', content: textBuffer });
      textBuffer = '';
    }
  };

  while (i < str.length) {
    // "\$5" is money, not the start of math
    if (str[i] === '\\' && str[i + 1] === '$') {
      textBuffer += '$';
      i += 2;
      continue;
    }

    if (str[i] === '\\' && str.startsWith('begin{', i + 1)) {
      const envNameMatch = str.slice(i).match(/^\\begin\{([^}]+)\}/);
      if (envNameMatch) {
        // Find matching \end{envName} — depth-aware to handle nested environments
        const beginTag = `\\begin{${envNameMatch[1]}}`;
        const endTag = `\\end{${envNameMatch[1]}}`;
        let depth = 1;
        let searchPos = i + beginTag.length;
        let endIdx = -1;

        while (searchPos < str.length && depth > 0) {
          const remaining = str.slice(searchPos);
          const nextBeginIdx = remaining.indexOf(beginTag);
          const nextEndIdx = remaining.indexOf(endTag);
          if (nextEndIdx === -1) break;
          if (nextBeginIdx !== -1 && nextBeginIdx < nextEndIdx) {
            depth++;
            searchPos += nextBeginIdx + beginTag.length;
          } else {
            depth--;
            if (depth === 0) endIdx = searchPos + nextEndIdx;
            searchPos += nextEndIdx + endTag.length;
          }
        }

        if (endIdx !== -1) {
          flushText();
          // KaTeX fails on display delimiters nested inside an environment
          const block = str.slice(i, endIdx + endTag.length).replace(/\\\[|\\\]|\$\$/g, ' ');
          segments.push({ type: 'displayMath', content: block });
          i = endIdx + endTag.length;
          continue;
        }
      }
    }

    if (str[i] === '$' && str[i + 1] === '$') {
      const end = str.indexOf('$$', i + 2);
      if (end !== -1) {
        flushText();
        segments.push({ type: 'displayMath', content: str.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }

    if (str[i] === '$') {
      const end = nextDollar(str, i + 1);
      if (end !== -1) {
        flushText();
        segments.push({ type: 'inlineMath', content: str.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (str[i] === '\\' && str[i + 1] === '(') {
      const end = str.indexOf('\\)', i + 2);
      if (end !== -1) {
        flushText();
        segments.push({ type: 'inlineMath', content: str.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }

    if (str[i] === '\\' && str[i + 1] === '[') {
      const end = str.indexOf('\\]', i + 2);
      if (end !== -1) {
        flushText();
        segments.push({ type: 'displayMath', content: str.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }

    if (str[i] === '!' && str[i + 1] === '[') {
      const altEnd = str.indexOf(']', i + 2);
      if (altEnd !== -1 && str[altEnd + 1] === '(') {
        const urlEnd = str.indexOf(')', altEnd + 2);
        if (urlEnd !== -1) {
          flushText();
          segments.push({ type: 'image', alt: str.slice(i + 2, altEnd), url: str.slice(altEnd + 2, urlEnd) });
          i = urlEnd + 1;
          continue;
        }
      }
    }

    if (str[i] === 'h' && /^https?:\/\//i.test(str.slice(i))) {
      const urlMatch = str.slice(i).match(/^(https?:\/\/[^\s)]+\.(?:png|jpg|jpeg|gif|webp|svg|bmp)(?:\?[^\s)]*)?)/i);
      if (urlMatch) {
        let url = urlMatch[1];
        if (/[.,!?;]$/.test(url)) url = url.slice(0, -1);
        flushText();
        segments.push({ type: 'image', alt: 'image', url });
        i += url.length;
        continue;
      }
    }

    textBuffer += str[i];
    i++;
  }
  flushText();

  return segments;
};
